import {
  Button,
  ButtonColor,
  ButtonWidth,
  compact,
  IconCheck,
  IconPhone,
  ToastDuration,
  ToastType,
  useToast,
} from '@leland-dev/leland-ui-library';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { type FormEvent, useEffect, useState } from 'react';

import { type Category } from '../../types/cmsTypes';
import phog from '../../utils/analytics/posthog/posthog';
import {
  CALENDLY_LINKS,
  HIDE_SUBSCRIBE_MODAL_KEY,
  NUM_CUSTOMERS_STR,
} from '../../utils/constants';
import { sanityCategoryIdToUrn } from '../../utils/sanity';
import { LelandLocalStorage } from '../../utils/storage';
import { addUtmParamsFromArticle } from '../../utils/utm';

import { useArticleCtaSendSeoEmailCollectionMutation } from './__generated-gql-types__/ArticleCTA.generated';

// Kept the same name as the old modal to preserve state of users who have already subscribed

interface ArticleCTAProps {
  categories?: Category[];
  articleSlug: Possible<string>;
  didSubscribe: boolean;
  setDidSubscribe: (val: boolean) => void;
}

export const ArticleCTA: React.FC<ArticleCTAProps> = ({
  categories,
  articleSlug,
  didSubscribe,
  setDidSubscribe,
}: ArticleCTAProps) => {
  const { showToast } = useToast();
  const router = useRouter();

  const [sendSeoEmailCollectionAction, { loading: isSubscribing }] =
    useArticleCtaSendSeoEmailCollectionMutation();
  const [email, setEmail] = useState<string>();
  const trimmedEmail = email?.trim().toLowerCase();

  const [isLoading, setIsLoading] = useState(true);

  // Used to prevent flash of loading state
  useEffect(() => {
    setIsLoading(false);
  }, []);

  const articleUrl = router.asPath;

  const categoryList = categories
    ?.map((category) => sanityCategoryIdToUrn(category._id))
    .filter(compact);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();

    const { errors } = await sendSeoEmailCollectionAction({
      variables: {
        email: trimmedEmail ?? '',
        categories: categoryList,
        articleUrl,
      },
    });

    phog.capture('article_page--email_list_subscribe', {
      categories: categoryList,
      articleUrl,
    });

    if (errors) {
      showToast({
        message: 'Something went wrong, please try again',
        type: ToastType.ERROR,
        duration: ToastDuration.LONG,
      });
    } else {
      setDidSubscribe(true);
      LelandLocalStorage.setItem(HIDE_SUBSCRIBE_MODAL_KEY, 'true');
      showToast({
        message: "You're in the loop",
        type: ToastType.SUCCESS,
        duration: ToastDuration.LONG,
      });
    }
  };

  if (isLoading) {
    return null;
  }

  return (
    <div className="flex flex-col items-start space-y-3 rounded-xl bg-mobile-hero bg-cover bg-center p-4 text-white lg:max-w-4xl lg:bg-desktop-hero">
      {didSubscribe ? (
        <>
          <div className="flex w-full items-center gap-2 border-b border-white/10 pb-4">
            <div className="rounded-full bg-leland-primary-extra-light p-2">
              <IconCheck className="size-4 text-leland-primary" />
            </div>
            <p className="text-xl font-medium">You&apos;re in the loop</p>
          </div>
          <h3 className="max-w-md text-xl font-medium">
            Start achieving your goals
          </h3>
          <p className="max-w-xl">
            Set up a call with a Leland team member to get exclusive deals and
            help achieve your goals
          </p>
          <Link
            href={addUtmParamsFromArticle(
              CALENDLY_LINKS.MEET_WITH_TEAM_MEMBER,
              articleSlug,
            )}
            className="w-full"
            id="article_cta--schedule_call"
          >
            <Button
              LeftIcon={() => <IconPhone className="size-4" />}
              label="Schedule a strategy call"
              buttonColor={ButtonColor.PRIMARY}
              rounded
              width={ButtonWidth.FULL}
              onClick={() => {
                phog.capture('article_page--schedule_call_button_click', {
                  categories: categoryList,
                  articleUrl,
                });
              }}
            />
          </Link>
        </>
      ) : (
        <>
          <h3 className="max-w-md text-xl font-medium">
            Stay in the loop. Go places.
          </h3>
          <p className="max-w-xl">
            Join {NUM_CUSTOMERS_STR} others and receive free resources,
            exclusive event invites, and personalized support to reach your
            goals.
          </p>
          <form onSubmit={onSubmit} className="w-full space-y-3">
            <input
              required
              className="w-full rounded-md border border-leland-gray-stroke p-3 text-leland-gray-dark"
              type="email"
              name="email"
              value={email}
              onChange={(e) => setEmail(e.currentTarget.value)}
              placeholder="Type your email"
            />
            <Button
              type="submit"
              label={isSubscribing ? 'Submitting...' : 'Submit'}
              buttonColor={ButtonColor.PRIMARY}
              width={ButtonWidth.FULL}
              id="article_cta--submit_email_list_subscribe"
            />
          </form>
        </>
      )}
    </div>
  );
};
